// * Importing necessary modules
import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  FormEvent,
} from "react";

//* Importing necessary react-router-dom modules
import { useNavigate, useParams } from "react-router-dom";

// * Importing necessary react-bootstrap modules
import {
  Form,
  InputGroup,
} from "react-bootstrap";

// * Importing necessary react-select modules
import AsyncSelect from "react-select/async";

// * Importing necessary react-icons modules
import { RiArrowGoBackLine } from "react-icons/ri";

// * Importing necessary moment modules
import moment from "moment";

// * Importing necessary components
import DefaultButton from "../../../../component/base/DefaultButton";
import { Attention } from "../../../../component/base/Attention";
import { Searchbar } from "../../../../component/list/searchbar";
import { Actions } from "../../../../component/list/actions";

// * Importing necessary hooks
import { useTests } from "../../../../hooks/test/useTests";
import { useApplicationTests } from "../../../../hooks/applications/applicationsTests/useApplicationTest";

var debounceFn = require("lodash.debounce");

export function ApplicationManagerExamForm(props: { editMode?: boolean }) {
  const params = useParams();
  const applicationId = params.id || "";

  const token = localStorage.getItem("token") || "";

  // HOOKS para gerenciar Provas (Tests)
  const {
    test, 
    loading: loadingTests,
    error: errorTests,
    setSearch, 
  } = useTests(token);


  // HOOKS para gerenciar Provas já vinculadas à aplicação (ApplicationTests)
  const {
    applicationTests, 
    loading: loadingAppTests,
    error: errorAppTests,
    createApplicationTest, 
    deleteApplicationTest, 
  } = useApplicationTests(applicationId, token);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingSave, setLoadingSave] = useState<boolean>(false);
  const [searchForm, setSearchForm] = useState<string>(""); 
  const [examsSelected, setExamsSelected] = useState<Record<string, any>>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [examText, setExamText] = useState<string>(""); 

  const navigate = useNavigate();

  // Ao carregar, convertemos os registros vindos do backend (applicationTests)
  useEffect(() => {
    if (!applicationTests) return;
    const selectedObj: Record<string, any> = {};
    applicationTests.forEach((item) => {
      if (item.test) {
        const { id, name, createdAt, questions = [] } = item.test;
        selectedObj[id] = { id, name, createdAt, questions };
      }
    });
    setExamsSelected(selectedObj);
  }, [applicationTests]);

  // Adiciona uma prova ao estado local examsSelected
  const handleAddExam = useCallback((exam: any) => {
    setExamsSelected((prev) => ({
      ...prev,
      [exam.id]: exam,
    }));
  }, []);

  // Remove uma prova do estado local examsSelected
  const handleRemoveExam = useCallback((examId: string) => {
    setExamsSelected((prev) => {
      const newState = { ...prev };
      delete newState[examId];
      return newState;
    });
  }, []);

  // e filtra localmente a lista `test`.
  const searchTests = useCallback(
    (inputValue: string, callback: (options: any[]) => void) => {
      setSearch(inputValue);
      const filtered = test.filter((t) =>
        t.name.toLowerCase().includes(inputValue.toLowerCase())
      );
      callback(filtered);
    },
    [test, setSearch]
  );

  // Debounce para evitar várias chamadas simultâneas
  const debouncedSearchTests = useMemo(
    () => debounceFn(searchTests, 250),
    [searchTests]
  );

  // Filtro memoizado das provas que estão selecionadas (para exibir na tabela)
  const filteredExamsSelected = useMemo(() => {
    const lowerSearch = searchForm.toLowerCase();
    return Object.values(examsSelected).filter((item: any) =>
      item.name.toLowerCase().includes(lowerSearch)
    );
  }, [examsSelected, searchForm]);

  // Função principal para SALVAR as alterações
  const handleSave = useCallback(async () => {
    try {
      setLoadingSave(true);

      const existingTestIds = applicationTests.map((appTest) => appTest.testId);

      const selectedIds = Object.keys(examsSelected);

      for (const id of selectedIds) {
        if (!existingTestIds.includes(id)) {
          await createApplicationTest({ test_id: id });
        }
      }

      for (const appTest of applicationTests) {
        if (!selectedIds.includes(appTest.testId)) {
          await deleteApplicationTest(appTest.id);
        }
      }

      navigate(`/tests/view/${applicationId}?tab=2`);
    } catch (error) {
      console.error("Erro ao salvar provas:", error);
      setErrorMessage("Ocorreu um erro ao salvar as provas.");
    } finally {
      setLoadingSave(false);
    }
  }, [
    applicationTests,
    examsSelected,
    applicationId,
    navigate,
    createApplicationTest,
    deleteApplicationTest,
  ]);

  // Submit do formulário principal
  const onSubmitForm = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      void handleSave();
    },
    [handleSave]
  );

  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-body" style={{ overflow: "visible" }}>
            <h4
              className="mt-0 header-title"
              style={{ fontSize: "1.5rem", color: "black" }}
            >
              Adicionar/Remover Provas
            </h4>
            <p style={{ fontSize: "1rem", color: "black" }}>
              Cadastre/Edite as provas da aplicação
            </p>
            <br />

            {/* Exibe eventual erro de API ou erro local */}
            {(errorAppTests || errorTests || errorMessage) && (
              <div style={{ display: "flex", marginBottom: 15, width: "100%" }}>
                <div className="alert alert-danger w-100 mb-0" role="alert">
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="alert"
                    aria-label="Close"
                    onClick={() => {
                      setErrorMessage(null);
                    }}
                  ></button>
                  <p style={{ marginBottom: 0, whiteSpace: "pre-line" }}>
                    {errorMessage || errorAppTests || errorTests}
                  </p>
                </div>
              </div>
            )}

            {/* Loading geral */}
            {(loadingAppTests || loadingTests) && (
              <div className="row mt-2">
                <div
                  className="col-12 d-flex justify-content-center align-items-center"
                  style={{ marginTop: "2rem" }}
                >
                  <div className="spinner-border text-primary" role="status" />
                </div>
              </div>
            )}

            {/* Conteúdo principal (somente se não está carregando) */}
            {!loadingAppTests && !loadingTests && (
              <Form onSubmit={onSubmitForm} style={{ position: "relative" }}>
                <InputGroup>
                  <div className="flex fdcolumn flex1 margin-bottom">
                    <Form.Label>
                      Prova <Attention />
                    </Form.Label>

                    <AsyncSelect
                      getOptionLabel={(option: any) => option.name}
                      getOptionValue={(option: any) => option.id}
                      placeholder="Digite parte do nome da prova..."
                      noOptionsMessage={() => "Nenhuma opção encontrada"}
                      onChange={(selected: any) => {
                        if (selected) {
                          handleAddExam(selected);
                        }
                      }}
                      onInputChange={(inputValue: string) => {
                        setExamText(inputValue);
                        return inputValue;
                      }}
                      cacheOptions
                      loadOptions={debouncedSearchTests}
                      defaultOptions={test}
                      value={null} // para não "fixar" a seleção
                    />
                  </div>
                </InputGroup>

                <br />

                <Form.Label>
                  Provas <Attention />
                </Form.Label>
                <div className="row">
                  <div className="col-12">
                    <Searchbar
                      search_input_value={searchForm}
                      search_text="Encontre uma prova"
                      onChangeSearchInput={(value: any) => setSearchForm(value)}
                      onSearchButtonClick={() => {
                      }}
                    />

                    <div className="table-rep-plugin">
                      <div
                        className="table-responsive mb-0"
                        data-pattern="priority-columns"
                      >
                        <table
                          id="tech-companies-1"
                          className="table table-striped"
                        >
                          <thead>
                            <tr>
                              <th>Nome</th>
                              <th>Número de Questões</th>
                              <th>Criada em</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredExamsSelected.map(
                              (item: any, idx: number) => (
                                <tr key={item.id}>
                                  <td>{item.name}</td>
                                  <td>{item.questions?.count ?? 0}</td>
                                  <td>
                                    {moment(item.createdAt).format(
                                      "DD/MM/YYYY HH:mm"
                                    )}
                                  </td>
                                  <td>
                                    <Actions
                                      actionItem={item}
                                      onDeleteClick={() => {
                                        handleRemoveExam(item.id);
                                      }}
                                      hideManager
                                      hideEdit
                                      hideDelete={false}
                                    />
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {filteredExamsSelected.length === 0 && (
                      <div className="row w-100 mt-3">
                        <div className="col-12">
                          <p style={{ textAlign: "center" }}>
                            Nenhuma prova vinculada/encontrada
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <br />

                {/* Botões (salvar ou voltar) */}
                {!loadingSave && (
                  <div className="w100 inline-flex jcsb mt-2">
                    <DefaultButton
                      bg="secondary"
                      text="Voltar"
                      icon={<RiArrowGoBackLine />}
                      to={`/tests/view/${applicationId}`}
                    />

                    <DefaultButton
                      type="submit"
                      bg="confirm"
                      text="Salvar provas"
                      disabled={loadingSave}
                      loadingText="Salvando provas"
                      loading={loadingSave}
                    />
                  </div>
                )}

                {loadingSave && (
                  <div className="row mt-2">
                    <div className="col-12 d-flex justify-content-center align-items-center">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      />
                    </div>
                  </div>
                )}
              </Form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
